import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getCheckoutStatus } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useT } from "@/context/LanguageContext";
import { TESTIDS } from "@/constants/testIds";

const CheckoutSuccess = () => {
  const { t } = useT();
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id");
  const [status, setStatus] = useState("polling");
  const [tx, setTx] = useState(null);
  const cart = useCart();

  useEffect(() => {
    if (!sessionId) { setStatus("failed"); return; }
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const data = await getCheckoutStatus(sessionId);
        setTx(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          cart.clear();
          return;
        }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (attempts >= 8) { setStatus("pending"); return; }
        setTimeout(tick, 2000);
      } catch (_e) {
        if (attempts >= 5) setStatus("failed");
        else setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <div className="min-h-screen pt-32 px-6 md:px-12 pb-24 flex flex-col items-center text-center" data-testid={TESTIDS.checkoutStatusBox}>
      <div className="font-typewriter text-[10px] tracking-[0.32em] uppercase text-[#6B6862]">{t("checkout.eyebrow")}</div>

      {status === "polling" && (
        <>
          <h1 className="font-serif-display text-5xl md:text-7xl mt-4">{t("checkout.polling")}</h1>
          <p className="mt-6 text-[#6B6862] italic max-w-md">{t("checkout.polling.body")}</p>
        </>
      )}
      {status === "paid" && (
        <>
          <h1 className="font-serif-display text-5xl md:text-7xl mt-4 leading-tight">{t("checkout.paid")}</h1>
          <p className="mt-6 text-[#6B6862] max-w-md font-sans-clean">{t("checkout.paid.body")}</p>
          {tx && <div className="mt-6 font-typewriter text-xs tracking-[0.2em] uppercase text-[#2C2C2C]">PAID — €{tx.amount?.toFixed(2)} {tx.currency?.toUpperCase()}</div>}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to={`/order/status/${sessionId}`} className="btn-orange">{t("checkout.paid.viewOrder")}</Link>
            <Link to="/" className="text-xs underline underline-offset-2 text-[#8A7568] hover:text-[#241C18]">
              {t("checkout.paid.return")}
            </Link>
          </div>
        </>
      )}
      {status === "expired" && (
        <>
          <h1 className="font-serif-display text-5xl mt-4">{t("checkout.expired")}</h1>
          <p className="mt-6 text-[#6B6862]">{t("checkout.expired.body")}</p>
          <Link to="/" className="btn-ghost mt-10">{t("checkout.expired.back")}</Link>
        </>
      )}
      {(status === "failed" || status === "pending") && (
        <>
          <h1 className="font-serif-display text-5xl mt-4">{status === "pending" ? t("checkout.pending") : t("checkout.failed")}</h1>
          <p className="mt-6 text-[#6B6862] max-w-md">{status === "pending" ? t("checkout.pending.body") : t("checkout.failed.body")}</p>
          <Link to="/" className="btn-ghost mt-10">{t("checkout.expired.back")}</Link>
        </>
      )}
    </div>
  );
};

export default CheckoutSuccess;
